'use client';

import {useId} from 'react';

import classNames from 'classnames';

import {useI18n} from '@udemy-v2/i18n';
import {SelectableChip} from '@udemy-v2/react-form-components';

import styles from './category-filters.module.scss';

export interface CategoryFiltersProps {
    /**
     * Chip labels from the `filterable_categories` feature variable, in flag order.
     *
     * The flag is authoritative and there is no hardcoded allowlist (decision 2.3), so the
     * row is built to wrap at any count rather than to the 14 chips Figma happens to draw.
     */
    categories: string[];
    /** Currently selected category names. Empty means "All". */
    selected: ReadonlySet<string>;
    /** Adds or removes one category from the selection. */
    onToggle: (category: string) => void;
    /** Clears the selection, which is what "All" means. */
    onSelectAll: () => void;
    /** Merged onto the root, so the section owns the spacing around this block. */
    className?: string;
}

/**
 * Topic filter for the discover grid — Figma node 5260:156744 (desktop) and 5260:160521
 * (mobile), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A label and note above a wrapping row of selectable chips. Selection is multi-select and
 * filters the already-fetched set in memory, so there is no busy state to show: nothing
 * here makes a request.
 *
 * Presentational and props-only — {@link CommunityShowcase} owns the selection.
 */
export function CategoryFilters({
    categories,
    selected,
    onToggle,
    onSelectAll,
    className,
}: CategoryFiltersProps) {
    const {gettext} = useI18n();
    const labelId = useId();
    const noteId = useId();

    return (
        // The chips are checkboxes, so they need a group with the visible label as its
        // accessible name; the note is what the group is described by.
        <div
            className={classNames(styles['filters'], className)}
            role="group"
            aria-labelledby={labelId}
            aria-describedby={noteId}
            data-purpose="community-category-filters"
        >
            <span className={styles['label']} id={labelId}>
                {gettext('Select instructor communities by topic')}
            </span>
            <span className={styles['note']} id={noteId}>
                {gettext('Choose all that apply')}
            </span>
            <ul className={styles['chips']}>
                {/* "All" is not a category — it is the empty selection, so it is checked
                    exactly when nothing else is and clicking it clears the rest. */}
                <SelectableChip
                    className={styles['chip']}
                    size="small"
                    wrapperElement="li"
                    checked={selected.size === 0}
                    onChange={onSelectAll}
                    data-purpose="community-category-filter-all"
                >
                    {gettext('All')}
                </SelectableChip>
                {categories.map((category) => (
                    <SelectableChip
                        key={category}
                        className={styles['chip']}
                        size="small"
                        wrapperElement="li"
                        checked={selected.has(category)}
                        onChange={() => onToggle(category)}
                        data-purpose="community-category-filter"
                    >
                        {category}
                    </SelectableChip>
                ))}
            </ul>
        </div>
    );
}
